"use client"

import * as React from "react"
import {
  Check,
  ChevronsUpDown,
  GalleryVerticalEnd,
  Search,
  ChevronUp,
  LogOut,
  Settings,
  User,
  Home,
  FileText,
  Flag,
  BarChart3,
  Box,
  Building,
  Package,
} from "lucide-react"

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import {
  Sidebar,
  SidebarContent as SidebarContentPrimitive,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useAuthStore } from '@/stores/auth/store';
import { logoutUser } from '@/lib/api';
import { useNavigate, Link } from 'react-router-dom';
import { Outlet, useParams, Link as RouterLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getSiteSections } from "@/lib/api";

// Sample data for the sidebar
const data = {
  versions: ["1.0.1", "1.1.0-alpha", "2.0.0-beta1"],
  user: {
    name: "John Doe",
    email: "john.doe@example.com",
    avatar: "/placeholder.svg?height=32&width=32",
  },
  navMain: [
    {
      title: "Home",
      url: "/",
      icon: Home,
    },
    {
      title: "Authentications",
      url: "/authentications",
      icon: FileText,
    },
    {
      title: "Projects",
      url: "/sites",
      icon: Flag,
      isActive: true,
    },
    // {
    //   title: "Dashboard",
    //   url: "#",
    //   icon: BarChart3,
    // },
    // {
    //   title: "Products",
    //   url: "#",
    //   icon: Box,
    // },
    // {
    //   title: "Organization",
    //   url: "#",
    //   icon: Building,
    // },
  ],
}

// Create a separate component for the sidebar content
function SidebarContent() {
  const [selectedVersion, setSelectedVersion] = React.useState(data.versions[0])
  const { state } = useSidebar()
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  // Detect site slug from route
  const { siteSlug } = useParams<{ siteSlug?: string }>();
  // Fetch site sections if slug is present
  const { data: siteSections, isLoading: sectionsLoading, error: sectionsError } = useQuery({
    queryKey: ["siteSections", siteSlug],
    queryFn: () => siteSlug ? getSiteSections(siteSlug) : Promise.resolve([]),
    enabled: !!siteSlug,
  });

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch (e) {
      // Optionally show an error toast
    }
    logout();
    navigate('/login');
  };

  return (
    <>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  tooltip="Pullentity"
                >
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                    <GalleryVerticalEnd className="size-4" />
                  </div>
                  <div className="flex flex-col gap-0.5 leading-none">
                    <span className="font-semibold">Pullentity</span>
                    {/*<span className="">v{selectedVersion}</span>*/}
                  </div>
                  {/*<ChevronsUpDown className="ml-auto" />*/}
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              {/*<DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]" align="start">
                {data.versions.map((version) => (
                  <DropdownMenuItem key={version} onSelect={() => setSelectedVersion(version)}>
                    v{version} {version === selectedVersion && <Check className="ml-auto" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>*/}
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
        {state === "expanded" && (
          <form>
            <SidebarGroup className="py-0">
              <SidebarGroupContent className="relative">
                <Label htmlFor="search" className="sr-only">
                  Search
                </Label>
                <SidebarInput id="search" placeholder="Search the docs..." className="pl-8" />
                <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 select-none opacity-50" />
              </SidebarGroupContent>
            </SidebarGroup>
          </form>
        )}
      </SidebarHeader>
      <SidebarContentPrimitive>
        {/* Site General Options Sidebar Group */}
        {siteSlug && (
          <>
            <SidebarGroup>
              <SidebarHeader>
                <span className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-1">General Options</span>
              </SidebarHeader>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <RouterLink to={`/sites/${siteSlug}/settings`}>
                        <span className="mr-2"><Box className="size-5" /></span>
                        <span>Site configuration</span>
                      </RouterLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>

                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <RouterLink to={`/sites/${siteSlug}/pages`}>
                        <span className="mr-2"><FileText className="size-5" /></span>
                        <span>Pages</span>
                      </RouterLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>

                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <RouterLink to={`/sites/${siteSlug}/collaborators`}>
                        <span className="mr-2"><User className="size-5" /></span>
                        <span>Collaborators</span>
                      </RouterLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <RouterLink to={`/sites/${siteSlug}/posts`}>
                        <span className="mr-2"><FileText className="size-5" /></span>
                        <span>Blog</span>
                      </RouterLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <RouterLink to={`/sites/${siteSlug}/products`}>
                        <span className="mr-2"><Package className="size-5" /></span>
                        <span>Products</span>
                      </RouterLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <RouterLink to={`/sites/${siteSlug}/themes`}>
                        <span className="mr-2"><Flag className="size-5" /></span>
                        <span>Themes</span>
                      </RouterLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <RouterLink to={`/sites/${siteSlug}/deployments`}>
                        <span className="mr-2"><BarChart3 className="size-5" /></span>
                        <span>Deployments</span>
                      </RouterLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            {/* Site Sections Sidebar Group */}
            <SidebarGroup>
              <SidebarHeader>
                <span className="font-semibold text-sm uppercase tracking-wider">Site Menu</span>
              </SidebarHeader>
              <SidebarGroupContent>
                <SidebarMenu>
                  {sectionsLoading && (
                    <SidebarMenuItem>
                      <SidebarMenuButton disabled>
                        <span>Loading sections...</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  {sectionsError && (
                    <SidebarMenuItem>
                      <SidebarMenuButton variant="destructive">
                        <span>Error loading sections</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  {/*siteSections && siteSections.length > 0 && siteSections.map((section: any) => (
                    <SidebarMenuItem key={section.id}>
                      <SidebarMenuButton asChild tooltip={section.name}>
                        <RouterLink to={`/sites/${siteSlug}/sections/${section.slug}`}>
                          <span>{section.name}</span>
                        </RouterLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))*/}
                  {/*siteSections && siteSections.length === 0 && !sectionsLoading && (
                    <SidebarMenuItem>
                      <SidebarMenuButton disabled>
                        <span>No sections</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )*/}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {data.navMain.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={item.isActive} tooltip={item.title}>
                    <RouterLink to={item.url}>
                      <item.icon className="size-5" />
                      <span>{item.title}</span>
                    </RouterLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContentPrimitive>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg" tooltip="User Settings">
                  <Avatar className="size-8">
                    <AvatarImage src={user?.avatar || "/placeholder.svg"} alt={user?.email || "User"} />
                    <AvatarFallback>{user?.email ? user.email.charAt(0).toUpperCase() : "?"}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-0.5 text-left leading-none">
                    <span className="font-medium">{user ? user.email : "Guest"}</span>
                    <span className="text-xs text-muted-foreground">{user ? user.email : "Not signed in"}</span>
                  </div>
                  <ChevronUp className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-[--radix-dropdown-menu-trigger-width]">
                {user ? (

                  <>
                    <DropdownMenuItem>
                      <User className="mr-2 size-4" />
                      <span>Profile</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Settings className="mr-2 size-4" />
                      <span>Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="mr-2 size-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem asChild>
                    <Link to="/login">
                      <User className="mr-2 size-4" />
                      <span>Sign in</span>
                    </Link>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </>
  )
}

export default function SidebarExample() {
  return (
    <SidebarProvider>
      <Sidebar collapsible="icon">
        <SidebarContent />
      </Sidebar>
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem className="hidden md:block">
                <BreadcrumbLink href="#">Projects</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="hidden md:block" />
              <BreadcrumbItem>
                <BreadcrumbPage>Dashboard</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </header>
        <div className="flex flex-col gap-4 p-4 h-[calc(100vh-4rem)]">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
